import { useEffect, useRef } from 'react';
import { fetchLiveQuotes } from '../services/apiClient';

const POLL_INTERVAL_MS = 15_000;

/**
 * Returns true if US equity market is open (Mon–Fri, 9:30–16:00 ET).
 */
function isMarketOpen(): boolean {
  const now = new Date();
  const et = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const day = et.getDay();
  if (day === 0 || day === 6) return false;
  const mins = et.getHours() * 60 + et.getMinutes();
  return mins >= 9 * 60 + 30 && mins < 16 * 60;
}

/**
 * Poll live prices every 15 s during market hours and call back with results.
 * Pauses when the browser tab is hidden to save CPU/network.
 *
 * @param tickers  Array of tickers to poll (pass empty to disable).
 * @param onPrices Callback receiving { [ticker]: price } map.
 */
export function useMarketPolling(
  tickers: string[],
  onPrices: (prices: Record<string, number>) => void,
): void {
  const onPricesRef = useRef(onPrices);
  onPricesRef.current = onPrices;

  const tickerKey = tickers.join(',');

  useEffect(() => {
    if (!tickerKey) return;

    let active = true;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const poll = async () => {
      if (!active || document.hidden || !isMarketOpen()) return;
      const prices = await fetchLiveQuotes(tickerKey.split(','));
      if (active) onPricesRef.current(prices);
    };

    const start = () => {
      if (intervalId) clearInterval(intervalId);
      intervalId = setInterval(poll, POLL_INTERVAL_MS);
    };

    const onVisibility = () => {
      if (document.hidden) {
        if (intervalId) { clearInterval(intervalId); intervalId = null; }
      } else {
        poll(); // immediate refresh when tab becomes visible
        start();
      }
    };

    start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      active = false;
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [tickerKey]);
}
